<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { apiClient } from '@/api/client'
import type { AzioneConfig, PaginaConfig, Regola } from '@/composables/usePersonalizzazione'
import { scaricaBase64 } from '@/utils/download'

interface Props {
  config: PaginaConfig
  posizione: AzioneConfig['posizione']
  entitaId?: number | null
  entitaTipo?: string
  contesto?: Record<string, unknown>
  payload?: Record<string, unknown>
}

const props = withDefaults(defineProps<Props>(), {
  entitaId: null,
  entitaTipo: '',
  contesto: () => ({}),
  payload: () => ({}),
})

const emit = defineEmits<{
  (e: 'azione-completata', handlerId: string, risultato: RisultatoAzione): void
  (e: 'azione-fallita', handlerId: string, errore: string): void
}>()

interface RisultatoAzione {
  successo: boolean
  messaggio: string
  downloadUrl: string | null
  datiAggiuntivi: Record<string, unknown>
}

const { t } = useI18n()
const confirm = useConfirm()
const toast = useToast()
const inEsecuzione = ref<Record<string, boolean>>({})

function valutaCondizione(regola: Regola | null | undefined): boolean {
  if (!regola) return true
  return evalRegola(regola, props.contesto)
}

function evalRegola(regola: Regola, contesto: Record<string, unknown>): boolean {
  const r = regola as { op: string; campo?: string; valore?: unknown; regole?: Regola[]; regola?: Regola }
  switch (r.op) {
    case 'and': return (r.regole ?? []).every((sub) => evalRegola(sub, contesto))
    case 'or': return (r.regole ?? []).some((sub) => evalRegola(sub, contesto))
    case 'not': return r.regola ? !evalRegola(r.regola, contesto) : true
    case '=': return contesto[r.campo!] === r.valore
    case '!=': return contesto[r.campo!] !== r.valore
    default: return true
  }
}

function handlerEffettivo(azione: AzioneConfig): string {
  return props.config.overrideAzioni[azione.handlerId] ?? azione.handlerId
}

async function eseguiAzione(azione: AzioneConfig) {
  const handlerId = handlerEffettivo(azione)

  if (inEsecuzione.value[azione.id]) return
  inEsecuzione.value[azione.id] = true

  try {
    const preHook = props.config.hookAzioni[`pre:${handlerId}`]
    if (preHook) {
      // hook pre-azione (solo stringa identificativa, nessuna chiamata remota)
      console.debug('[AzioniDinamiche] pre-hook', preHook)
    }

    const { data } = await apiClient.post<RisultatoAzione>(`/api/v2/azioni/${handlerId}`, {
      entitaId: props.entitaId,
      entitaTipo: props.entitaTipo,
      payload: { ...props.payload, ...props.contesto },
    })

    if (data.successo) {
      toast.add({ severity: 'success', summary: t('layout.azioni.completata', 'Operazione completata'), detail: data.messaggio, life: 4000 })
      if (data.downloadUrl && isSafeUrl(data.downloadUrl)) {
        window.open(data.downloadUrl, '_blank')
      } else if (data.datiAggiuntivi?.base64 && data.datiAggiuntivi?.filename) {
        scaricaBase64(data.datiAggiuntivi.base64 as string, data.datiAggiuntivi.filename as string)
      }
      emit('azione-completata', handlerId, data)
    } else {
      toast.add({ severity: 'warn', summary: t('layout.azioni.attenzione', 'Attenzione'), detail: data.messaggio, life: 5000 })
      emit('azione-fallita', handlerId, data.messaggio)
    }

    const postHook = props.config.hookAzioni[`post:${handlerId}`]
    if (postHook) {
      console.debug('[AzioniDinamiche] post-hook', postHook)
    }
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : t('layout.azioni.erroreEsecuzione', "Errore nell'esecuzione")
    toast.add({ severity: 'error', summary: t('layout.azioni.errore', 'Errore'), detail: msg, life: 5000 })
    emit('azione-fallita', handlerId, msg)
  } finally {
    inEsecuzione.value[azione.id] = false
  }
}

function onClick(azione: AzioneConfig) {
  if (azione.conferma) {
    confirm.require({
      message: azione.conferma.messaggio,
      header: t('layout.azioni.conferma', 'Conferma'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('layout.azioni.conferma', 'Conferma'),
      rejectLabel: t('common.annulla', 'Annulla'),
      accept: () => eseguiAzione(azione),
    })
  } else {
    eseguiAzione(azione)
  }
}

function isSafeUrl(url: string): boolean {
  return url.startsWith('/') || url.startsWith('https://') || url.startsWith('http://')
}


const azioniVisibili = () =>
  props.config.azioniAggiuntive.filter(
    (a) => a.posizione === props.posizione && valutaCondizione(a.visibileSe),
  )
</script>

<template>
  <div
    v-if="azioniVisibili().length > 0"
    class="azioni-dinamiche"
  >
    <Button
      v-for="azione in azioniVisibili()"
      :key="azione.id"
      :label="azione.label"
      :icon="azione.icona"
      :outlined="azione.stile === 'outlined'"
      :text="azione.stile === 'text'"
      :raised="azione.stile === 'raised'"
      :loading="inEsecuzione[azione.id]"
      size="small"
      @click="onClick(azione)"
    />
  </div>
</template>

<style scoped>
.azioni-dinamiche {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}
</style>
